<template>
  <nav
    v-if="jumplinks?.length"
    ref="navRef"
    :aria-label="navAriaLabel"
    class="content-jumplinks"
    :class="{
      [`content-jumplinks--bg-${bgColor}`]: bgColor,
      'content-jumplinks--stuck': isStuck,
    }"
  >
    <SAppContainer :size>
      <div class="content-jumplinks__links">
        <SButton
          v-for="jumplink in jumplinks"
          :key="jumplink.id"
          :action-type="jumplink.id === activeId ? 'primary' : 'secondary'"
          :aria-current="jumplink.id === activeId ? 'true' : undefined"
          :href="`#${jumplink.id}`"
          :prefix-icon="jumplink.icon || undefined"
          size="sm"
        >
          {{ jumplink.label }}
        </SButton>
      </div>
    </SAppContainer>
  </nav>
</template>

<script setup lang="ts">
import { useElementBounding } from "@vueuse/core";
import { computed, useTemplateRef, watch } from "vue";

import type { Jumplink } from "@solvari/statamic";

import { SButton } from "@solvari/common-fe";
import { SAppContainer, useJumplinks } from "@solvari/components";
import { useI18nMainSiteProduct } from "@solvari/translations";

const props = withDefaults(
  defineProps<{
    bgColor?: "dark" | "light" | "primary";
    jumplinks?: Jumplink[];
    size?: "lg" | "md" | "sm";
  }>(),
  { jumplinks: () => [] },
);

const { tr } = useI18nMainSiteProduct();

const navAriaLabel = tr("main_site.product.jumplinks_nav_label");

const { activeId } = useJumplinks(() =>
  props.jumplinks.map((jumplink) => jumplink.id),
);

const navRef = useTemplateRef<HTMLElement>("navRef");
const { top } = useElementBounding(navRef);

const isStuck = computed(() => {
  if (!navRef.value) {
    return false;
  }
  const stickyTop = Number.parseFloat(getComputedStyle(navRef.value).top) || 0;

  return top.value <= stickyTop + 1;
});

watch(
  activeId,
  () => {
    // `nearest` on both axes: no move when already visible, and never scrolls the page.
    navRef.value?.querySelector('[aria-current="true"]')?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  },
  { flush: "post" },
);
</script>

<style lang="postcss">
.content-jumplinks {
  @apply sticky top-14 z-30 w-full self-start border-b border-transparent bg-white transition-colors;

  &--stuck {
    @apply border-slate-200;
  }

  &__links {
    @apply flex overflow-x-auto py-3 pl-px gap-2;

    .s-button {
      @apply shrink-0 whitespace-nowrap;
    }
  }

  &--bg-light {
    @apply bg-slate-50;
  }

  &--bg-dark {
    @apply bg-primary-100;
  }

  &--bg-primary {
    @apply bg-primary-700;
  }

  @screen 500 {
    @apply top-16;
  }

  @screen 1000 {
    @apply top-0;
  }
}

[data-jumplink] {
  @apply scroll-mt-36;

  @screen 1000 {
    @apply scroll-mt-20;
  }
}
</style>
